import { Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Fieldset, Input, Radios, UniversalSelect } from '@feathr/components';
import { flattenError } from '@feathr/hooks';

import type { IOtherActivityFieldOption } from '../GroupExportModal.utils';
import { getOtherActivityScopeOptions } from '../GroupExportModal.utils';
import TimeFrameAndEmailFields from '../TimeFrameAndEmailFields';
import type { IOtherActivitiesTabState } from './OtherActivitiesTab.hooks';

export interface IOtherActivitiesTabProps {
  state: IOtherActivitiesTabState;
}

function OtherActivitiesTab({ state }: Readonly<IOtherActivitiesTabProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const {
    exportName,
    exportNameHadFocus,
    exportNameError,
    selectedScope,
    isCustomFieldsPending,
    otherActivityFieldOptions,
    selectedFieldOptions,
    fieldsError,
    handleChangeExportName,
    handleExportNameBlur,
    handleChangeScope,
    handleSelectFields,
  } = state;

  const exportNameValidationError = exportNameHadFocus ? flattenError(exportNameError) : undefined;

  const scopeOptions = getOtherActivityScopeOptions(t).map((option) => ({
    id: option.id,
    name: option.label,
    tooltip: option.tooltip,
  }));

  function handleScopeChange(value?: string): void {
    if (value !== 'group' && value !== 'all') {
      return;
    }
    handleChangeScope(value);
  }

  return (
    <Fieldset>
      <Stack gap={theme.spacing['4']}>
        <Input
          isClearable={true}
          label={t('Export name')}
          onBlur={handleExportNameBlur}
          onChange={handleChangeExportName}
          type={'text'}
          validationError={exportNameValidationError}
          value={exportName}
        />
        <Radios
          label={t('Activity')}
          layout={'block'}
          name={'other-activity-scope'}
          onChange={handleScopeChange}
          options={scopeOptions}
          orientation={'horizontal'}
          value={selectedScope}
        />
        <UniversalSelect<IOtherActivityFieldOption>
          isLoading={isCustomFieldsPending}
          isMulti={true}
          label={t('Fields')}
          name={'other-activity-fields'}
          onSelectMulti={handleSelectFields}
          options={otherActivityFieldOptions}
          placeholder={t('Add a field')}
          validationError={flattenError(fieldsError)}
          value={selectedFieldOptions}
        />
        <TimeFrameAndEmailFields state={state} />
      </Stack>
    </Fieldset>
  );
}

export default OtherActivitiesTab;
